import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * LangContext — single source of truth for app-wide TR/EN selection.
 *
 * Phase: HTML lang persistence across pages (MovieMakerLanding, SoftwareShop).
 *
 * Resolution priority (high → low):
 *   1. URL query param `?lang=tr|en` (only on landing/software pages)
 *   2. localStorage 'mm-lang' (set when user explicitly toggles)
 *   3. Browser language (navigator.language) for first-time visitors
 *   4. Fallback: 'tr'
 *
 * Persists to localStorage on every change and broadcasts
 * 'mm-lang-changed' on window so non-React code (HtmlLangSync, etc.)
 * can stay in sync.
 */

const STORAGE_KEY = 'mm-lang';

const isValidLang = (val) => val === 'tr' || val === 'en';

const resolveInitialLang = (queryLang) => {
  if (isValidLang(queryLang)) return queryLang;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isValidLang(stored)) return stored;
  } catch {
    // localStorage unavailable (SSR / sandbox) — fall through
  }
  const browser = (typeof navigator !== 'undefined' && navigator.language || 'en').toLowerCase();
  return browser.startsWith('tr') ? 'tr' : 'en';
};

const LangContext = createContext(null);

/**
 * LangProvider must be mounted INSIDE <Router> because it reads
 * URL search params via react-router.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.syncUrlQuery=true]
 *        If true, switching language updates ?lang= in the URL when
 *        the current route supports it (any route that already reads
 *        `?lang=` will reflect the change). For ShopLayout pages that
 *        do not honour ?lang=, callers should pass false OR rely on
 *        localStorage persistence only.
 */
export const LangProvider = ({ children, syncUrlQuery = true }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryLang = searchParams.get('lang');
  const [lang, setLangState] = useState(() => resolveInitialLang(queryLang));

  // Re-sync if the URL's ?lang= changes from outside (popstate, pushstate)
  useEffect(() => {
    if (isValidLang(queryLang) && queryLang !== lang) {
      setLangState(queryLang);
      try { localStorage.setItem(STORAGE_KEY, queryLang); } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryLang]);

  // Listen for non-React-driven language changes (e.g. legacy switchLang
  // code paths that fire `mm-lang-changed` directly).
  useEffect(() => {
    const handleExternal = (e) => {
      const next = e && e.detail && e.detail.lang;
      if (isValidLang(next) && next !== lang) setLangState(next);
    };
    window.addEventListener('mm-lang-changed', handleExternal);
    return () => window.removeEventListener('mm-lang-changed', handleExternal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!isValidLang(next) || next === lang) return;
    setLangState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* ignore */ }
    // Broadcast for HtmlLangSync and any non-React listeners.
    window.dispatchEvent(new CustomEvent('mm-lang-changed', { detail: { lang: next } }));
    // Optional: reflect in URL query. Other pages ignore ?lang=,
    // but on / and /software it's the canonical hint.
    if (syncUrlQuery) {
      try {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('lang', next);
        setSearchParams(newParams, { replace: true });
      } catch {
        // Some route contexts may not have a router — safe to ignore.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, searchParams, setSearchParams, syncUrlQuery]);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

/**
 * Hook — read the current language and the setter from context.
 * Falls back to a no-op stub if used outside a <LangProvider> (e.g.
 * during isolated unit tests) so consumers never crash.
 */
export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) {
    return { lang: 'tr', setLang: () => {} };
  }
  return ctx;
};

export default LangContext;